/**
 * What a bulk merge actually did to one PR. `alreadyMerged` means GitHub
 * reported the PR as merged before we asked — the merge call was skipped, so
 * nothing new was triggered for it in this run.
 */
export interface MergeOutcome {
  componentName: string;
  prNumber: number;
  alreadyMerged: boolean;
}

/**
 * Components whose Cloud Build the progress panel should follow: only the ones
 * this run actually merged. An already-merged PR's build fired for the earlier
 * merge — before this run's `since` — so tracking it would leave the panel on
 * "waiting for build" until the run times out.
 */
export function componentsToTrack(outcomes: MergeOutcome[]): string[] {
  return Array.from(
    new Set(
      outcomes
        .filter(outcome => !outcome.alreadyMerged)
        .map(outcome => outcome.componentName),
    ),
  );
}

function prCount(n: number): string {
  return `${n} ${n === 1 ? 'PR' : 'PRs'}`;
}

/** Bulk-merge result line, naming any PRs that were already merged. */
export function mergeSummaryMessage(
  label: string,
  outcomes: MergeOutcome[],
): string {
  const skipped = outcomes.filter(outcome => outcome.alreadyMerged).length;
  const done = outcomes.length - skipped;
  if (skipped === 0) {
    return `${label} completed for ${prCount(done)}.`;
  }
  const were = skipped === 1 ? 'was' : 'were';
  const are = skipped === 1 ? 'is' : 'are';
  return (
    `${label} completed for ${prCount(done)} ` +
    `(${prCount(skipped)} ${were} already merged and ${are} not tracked).`
  );
}
